import { createSlice } from '@reduxjs/toolkit';

import i18n, { LOCAL_STORAGE_LANGUAGE_KEY } from '@/i18next';
import routes from '@/routes';
import {
	handleAsyncThunkActionState,
	initialAsyncState,
	isAsyncThunkActionWithPrefix
} from '@/store/utils';

import * as thunks from './thunks';
import { getLocalStorageUser } from './utils';

export const LOCAL_STORAGE_CURRENCY_KEY = 'currency';

export const TAX_PAYER_TYPE = {
	COMPANY: 'company',
	PRIVATE: 'private'
};

export const AUTH_MODAL_TYPES = {
	EDITOR_SIGN_UP: 'editorSignUp',
	LOGIN: 'login',
	SIGN_UP: 'signUp',
	RECOVER_PASSWORD: 'recoverPassword',
	CHANGE_PASSWORD: 'changePassword',
	ACCOUNT_CLOSED: 'accountClosed'
};

export const AUTH_MODAL_SCOPES = {
	COMPRESS: 'compress',
	DOWNLOAD: 'download',
	NOTES: 'notes',
	SHARE: 'share'
};

export const REDIRECT_SCOPES = {
	[AUTH_MODAL_SCOPES.COMPRESS]: {
		register: routes.fastCheckout
	},
	[AUTH_MODAL_SCOPES.SHARE]: {
		register: routes.fastCheckout
	},
	[AUTH_MODAL_SCOPES.DOWNLOAD]: {
		register: routes.fastCheckout
	},
	[AUTH_MODAL_SCOPES.NOTES]: {
		register: routes.fastCheckout
	},
	subscribe: {
		login: routes.myDocuments,
		register: routes.checkout
	}
};

const user = getLocalStorageUser();

const initialState = {
	currentTab: 'generalInfo',
	language: i18n.language,
	user,
	login: initialAsyncState,
	signUp: initialAsyncState,
	taxInformation: null,
	paymentMethod: null,
	subscriptionInfo: null,
	billingInfoReady: false,
	fetchUser: initialAsyncState,
	loggedIn: !!user,
	timezone: '',
	currency: localStorage.getItem(LOCAL_STORAGE_CURRENCY_KEY),
	currencies: [],
	authModalOpen: false,
	authModalType: AUTH_MODAL_TYPES.LOGIN,
	authModalRedirect: null,
	fetchBillingInfo: initialAsyncState,
	fetchCurrencies: initialAsyncState,
	isModalUpgradePlanOpen: false,
	modalUpgradePlanScope: null,
	userReady: false,
	authModalScope: null,
	priceCurrency: null
};

const profileSlice = createSlice(
	{
		name: 'profile',
		initialState,
		reducers: {
			setLanguage(state, action) {
				localStorage.setItem(LOCAL_STORAGE_LANGUAGE_KEY, action.payload);
				state.language = action.payload;
			},
			setCurrency(state, action) {
				localStorage.setItem(LOCAL_STORAGE_CURRENCY_KEY, action.payload);
				state.currency = action.payload;
			},
			setTimezone(state, action) {
				state.timezone = action.payload;
			},
			setCountry(state, action) {
				state.user.country = action.payload;
			},
			setDailyLimitReached(state, action) {
				state.user.dailyLimitReached = action.payload;
			},
			setPaymentMethod(state, action) {
				state.paymentMethod = action.payload;
			},
			setPriceCurrency(state, action) {
				state.priceCurrency = action.payload;
			},
			setCurrentTab(state, action) {
				state.currentTab = action.payload;
			},
			setIsModalUpgradePlanOpen(state, action) {
				state.isModalUpgradePlanOpen = action.payload;
			},
			setModalUpgradePlanScope(state, action) {
				state.modalUpgradePlanScope = action.payload;
			},
			openAuthModal(state, action) {
				const {
					type,
					redirect,
					scope,
					state: redirectState
				} = action.payload;
				state.authModalOpen = true;
				state.authModalType = type;

				if (redirect) {
					state.authModalRedirect = {
						to: redirect,
						state: redirectState ?? undefined
					};
				}

				if (scope) {
					state.authModalScope = scope;
				}
			},
			closeAuthModal(state) {
				return {
					...state,
					authModalOpen: false,
					login: initialAsyncState,
					signUp: initialAsyncState,
					authModalRedirect: null,
					authModalScope: null
				};
			},
			setLocalTaxInfo(state, action) {
				return {
					...state,
					user: {
						...(state.user || {}),
						taxInformation: {
							...state.user?.taxInformation,
							...action.payload
						}
					}
				};
			},
			clearBillingData(state) {
				state.subscriptionInfo = null;
				state.fetchBillingInfo = initialAsyncState;
				state.billingInfoReady = false;
			}
		},
		extraReducers(builder) {
			builder.addCase(thunks.fetchCurrencies.fulfilled, (state, action) => {
				state.currencies = action.payload;
				state.currenciesReady = true;
			});

			builder.addCase(thunks.fetchUser.fulfilled, (state, action) => {
				state.user = action.payload;
				state.user.taxInformation = action.payload.taxInformation || {};
				state.loggedIn = true;
				// flag to check if user was already fetched at least once
				state.userReady = true;

				if (action.payload.language) {
					state.language = action.payload.language || i18n.language;
				}
			});

			builder.addCase(thunks.fetchUser.rejected, (state, action) => {
				if (action.payload?.status === 401) {
					state.loggedIn = false;
					state.user = null;
					localStorage.removeItem('user');
				}
			});

			builder.addCase(thunks.deleteUser.fulfilled, state => {
				state.user = null;
				state.loggedIn = false;
			});

			builder.addCase(thunks.fetchBillingInfo.fulfilled, (state, action) => {
				state.paymentMethod = action.payload.paymentMethod;
				state.subscriptionInfo = action.payload.subscription;
				state.billingInfoReady = true;

				if (action.payload.subscriptionInfo?.status) {
					state.user.subscriptionStatus =
						action.payload.subscriptionInfo.status;
				}
			});

			builder.addCase(thunks.logout.fulfilled, state => {
				state.user = null;
				state.loggedIn = false;
				state.paymentMethod = null;
				state.subscriptionInfo = null;
				state.priceCurrency = null;
			});

			builder.addMatcher(
				isAsyncThunkActionWithPrefix('profile'),
				handleAsyncThunkActionState()
			);
		}
	},

	initialState
);

export const {
	setLanguage,
	setCurrency,
	setTimezone,
	setCountry,
	setDailyLimitReached,
	setPaymentMethod,
	openAuthModal,
	closeAuthModal,
	setCurrentTab,
	setLocalTaxInfo,
	setIsModalUpgradePlanOpen,
	setModalUpgradePlanScope,
	clearBillingData,
	setPriceCurrency
} = profileSlice.actions;

export default profileSlice.reducer;
